// geoNames — bilingual (中文 + English) display names for the /admin 数据看板
// (dashboard) geo breakdowns. The analytics BFF returns raw codes/English names
// from Cloudflare (country = ISO 3166-1 alpha-2 code; region/city = English).
// This module is FRONTEND-ONLY presentation: it maps those into "<zh> <en>"
// labels without touching the BFF or the stored data.
//
// Format rule (all helpers): Chinese-first then English — "中国 China". When the
// Chinese and English forms are identical, or no Chinese form exists, only the
// single value is shown (no duplication).

/** ISO 3166-1 alpha-2 → {zh, en}. Keys are UPPERCASE. Complete (~249 codes). */
export const COUNTRY_NAMES: Record<string, { zh: string; en: string }> = {
  AD: { zh: "安道尔", en: "Andorra" },
  AE: { zh: "阿联酋", en: "United Arab Emirates" },
  AF: { zh: "阿富汗", en: "Afghanistan" },
  AG: { zh: "安提瓜和巴布达", en: "Antigua and Barbuda" },
  AI: { zh: "安圭拉", en: "Anguilla" },
  AL: { zh: "阿尔巴尼亚", en: "Albania" },
  AM: { zh: "亚美尼亚", en: "Armenia" },
  AO: { zh: "安哥拉", en: "Angola" },
  AQ: { zh: "南极洲", en: "Antarctica" },
  AR: { zh: "阿根廷", en: "Argentina" },
  AS: { zh: "美属萨摩亚", en: "American Samoa" },
  AT: { zh: "奥地利", en: "Austria" },
  AU: { zh: "澳大利亚", en: "Australia" },
  AW: { zh: "阿鲁巴", en: "Aruba" },
  AX: { zh: "奥兰群岛", en: "Åland Islands" },
  AZ: { zh: "阿塞拜疆", en: "Azerbaijan" },
  BA: { zh: "波斯尼亚和黑塞哥维那", en: "Bosnia and Herzegovina" },
  BB: { zh: "巴巴多斯", en: "Barbados" },
  BD: { zh: "孟加拉国", en: "Bangladesh" },
  BE: { zh: "比利时", en: "Belgium" },
  BF: { zh: "布基纳法索", en: "Burkina Faso" },
  BG: { zh: "保加利亚", en: "Bulgaria" },
  BH: { zh: "巴林", en: "Bahrain" },
  BI: { zh: "布隆迪", en: "Burundi" },
  BJ: { zh: "贝宁", en: "Benin" },
  BL: { zh: "圣巴泰勒米", en: "Saint Barthélemy" },
  BM: { zh: "百慕大", en: "Bermuda" },
  BN: { zh: "文莱", en: "Brunei" },
  BO: { zh: "玻利维亚", en: "Bolivia" },
  BQ: { zh: "荷属加勒比", en: "Caribbean Netherlands" },
  BR: { zh: "巴西", en: "Brazil" },
  BS: { zh: "巴哈马", en: "Bahamas" },
  BT: { zh: "不丹", en: "Bhutan" },
  BV: { zh: "布维岛", en: "Bouvet Island" },
  BW: { zh: "博茨瓦纳", en: "Botswana" },
  BY: { zh: "白俄罗斯", en: "Belarus" },
  BZ: { zh: "伯利兹", en: "Belize" },
  CA: { zh: "加拿大", en: "Canada" },
  CC: { zh: "科科斯（基林）群岛", en: "Cocos (Keeling) Islands" },
  CD: { zh: "刚果（金）", en: "DR Congo" },
  CF: { zh: "中非", en: "Central African Republic" },
  CG: { zh: "刚果（布）", en: "Congo" },
  CH: { zh: "瑞士", en: "Switzerland" },
  CI: { zh: "科特迪瓦", en: "Côte d'Ivoire" },
  CK: { zh: "库克群岛", en: "Cook Islands" },
  CL: { zh: "智利", en: "Chile" },
  CM: { zh: "喀麦隆", en: "Cameroon" },
  CN: { zh: "中国", en: "China" },
  CO: { zh: "哥伦比亚", en: "Colombia" },
  CR: { zh: "哥斯达黎加", en: "Costa Rica" },
  CU: { zh: "古巴", en: "Cuba" },
  CV: { zh: "佛得角", en: "Cape Verde" },
  CW: { zh: "库拉索", en: "Curaçao" },
  CX: { zh: "圣诞岛", en: "Christmas Island" },
  CY: { zh: "塞浦路斯", en: "Cyprus" },
  CZ: { zh: "捷克", en: "Czechia" },
  DE: { zh: "德国", en: "Germany" },
  DJ: { zh: "吉布提", en: "Djibouti" },
  DK: { zh: "丹麦", en: "Denmark" },
  DM: { zh: "多米尼克", en: "Dominica" },
  DO: { zh: "多米尼加", en: "Dominican Republic" },
  DZ: { zh: "阿尔及利亚", en: "Algeria" },
  EC: { zh: "厄瓜多尔", en: "Ecuador" },
  EE: { zh: "爱沙尼亚", en: "Estonia" },
  EG: { zh: "埃及", en: "Egypt" },
  EH: { zh: "西撒哈拉", en: "Western Sahara" },
  ER: { zh: "厄立特里亚", en: "Eritrea" },
  ES: { zh: "西班牙", en: "Spain" },
  ET: { zh: "埃塞俄比亚", en: "Ethiopia" },
  FI: { zh: "芬兰", en: "Finland" },
  FJ: { zh: "斐济", en: "Fiji" },
  FK: { zh: "福克兰群岛", en: "Falkland Islands" },
  FM: { zh: "密克罗尼西亚", en: "Micronesia" },
  FO: { zh: "法罗群岛", en: "Faroe Islands" },
  FR: { zh: "法国", en: "France" },
  GA: { zh: "加蓬", en: "Gabon" },
  GB: { zh: "英国", en: "United Kingdom" },
  GD: { zh: "格林纳达", en: "Grenada" },
  GE: { zh: "格鲁吉亚", en: "Georgia" },
  GF: { zh: "法属圭亚那", en: "French Guiana" },
  GG: { zh: "根西", en: "Guernsey" },
  GH: { zh: "加纳", en: "Ghana" },
  GI: { zh: "直布罗陀", en: "Gibraltar" },
  GL: { zh: "格陵兰", en: "Greenland" },
  GM: { zh: "冈比亚", en: "Gambia" },
  GN: { zh: "几内亚", en: "Guinea" },
  GP: { zh: "瓜德罗普", en: "Guadeloupe" },
  GQ: { zh: "赤道几内亚", en: "Equatorial Guinea" },
  GR: { zh: "希腊", en: "Greece" },
  GS: { zh: "南乔治亚和南桑威奇群岛", en: "South Georgia" },
  GT: { zh: "危地马拉", en: "Guatemala" },
  GU: { zh: "关岛", en: "Guam" },
  GW: { zh: "几内亚比绍", en: "Guinea-Bissau" },
  GY: { zh: "圭亚那", en: "Guyana" },
  HK: { zh: "中国香港", en: "Hong Kong" },
  HM: { zh: "赫德岛和麦克唐纳群岛", en: "Heard & McDonald Islands" },
  HN: { zh: "洪都拉斯", en: "Honduras" },
  HR: { zh: "克罗地亚", en: "Croatia" },
  HT: { zh: "海地", en: "Haiti" },
  HU: { zh: "匈牙利", en: "Hungary" },
  ID: { zh: "印度尼西亚", en: "Indonesia" },
  IE: { zh: "爱尔兰", en: "Ireland" },
  IL: { zh: "以色列", en: "Israel" },
  IM: { zh: "马恩岛", en: "Isle of Man" },
  IN: { zh: "印度", en: "India" },
  IO: { zh: "英属印度洋领地", en: "British Indian Ocean Territory" },
  IQ: { zh: "伊拉克", en: "Iraq" },
  IR: { zh: "伊朗", en: "Iran" },
  IS: { zh: "冰岛", en: "Iceland" },
  IT: { zh: "意大利", en: "Italy" },
  JE: { zh: "泽西", en: "Jersey" },
  JM: { zh: "牙买加", en: "Jamaica" },
  JO: { zh: "约旦", en: "Jordan" },
  JP: { zh: "日本", en: "Japan" },
  KE: { zh: "肯尼亚", en: "Kenya" },
  KG: { zh: "吉尔吉斯斯坦", en: "Kyrgyzstan" },
  KH: { zh: "柬埔寨", en: "Cambodia" },
  KI: { zh: "基里巴斯", en: "Kiribati" },
  KM: { zh: "科摩罗", en: "Comoros" },
  KN: { zh: "圣基茨和尼维斯", en: "Saint Kitts and Nevis" },
  KP: { zh: "朝鲜", en: "North Korea" },
  KR: { zh: "韩国", en: "South Korea" },
  KW: { zh: "科威特", en: "Kuwait" },
  KY: { zh: "开曼群岛", en: "Cayman Islands" },
  KZ: { zh: "哈萨克斯坦", en: "Kazakhstan" },
  LA: { zh: "老挝", en: "Laos" },
  LB: { zh: "黎巴嫩", en: "Lebanon" },
  LC: { zh: "圣卢西亚", en: "Saint Lucia" },
  LI: { zh: "列支敦士登", en: "Liechtenstein" },
  LK: { zh: "斯里兰卡", en: "Sri Lanka" },
  LR: { zh: "利比里亚", en: "Liberia" },
  LS: { zh: "莱索托", en: "Lesotho" },
  LT: { zh: "立陶宛", en: "Lithuania" },
  LU: { zh: "卢森堡", en: "Luxembourg" },
  LV: { zh: "拉脱维亚", en: "Latvia" },
  LY: { zh: "利比亚", en: "Libya" },
  MA: { zh: "摩洛哥", en: "Morocco" },
  MC: { zh: "摩纳哥", en: "Monaco" },
  MD: { zh: "摩尔多瓦", en: "Moldova" },
  ME: { zh: "黑山", en: "Montenegro" },
  MF: { zh: "法属圣马丁", en: "Saint Martin" },
  MG: { zh: "马达加斯加", en: "Madagascar" },
  MH: { zh: "马绍尔群岛", en: "Marshall Islands" },
  MK: { zh: "北马其顿", en: "North Macedonia" },
  ML: { zh: "马里", en: "Mali" },
  MM: { zh: "缅甸", en: "Myanmar" },
  MN: { zh: "蒙古", en: "Mongolia" },
  MO: { zh: "中国澳门", en: "Macao" },
  MP: { zh: "北马里亚纳群岛", en: "Northern Mariana Islands" },
  MQ: { zh: "马提尼克", en: "Martinique" },
  MR: { zh: "毛里塔尼亚", en: "Mauritania" },
  MS: { zh: "蒙特塞拉特", en: "Montserrat" },
  MT: { zh: "马耳他", en: "Malta" },
  MU: { zh: "毛里求斯", en: "Mauritius" },
  MV: { zh: "马尔代夫", en: "Maldives" },
  MW: { zh: "马拉维", en: "Malawi" },
  MX: { zh: "墨西哥", en: "Mexico" },
  MY: { zh: "马来西亚", en: "Malaysia" },
  MZ: { zh: "莫桑比克", en: "Mozambique" },
  NA: { zh: "纳米比亚", en: "Namibia" },
  NC: { zh: "新喀里多尼亚", en: "New Caledonia" },
  NE: { zh: "尼日尔", en: "Niger" },
  NF: { zh: "诺福克岛", en: "Norfolk Island" },
  NG: { zh: "尼日利亚", en: "Nigeria" },
  NI: { zh: "尼加拉瓜", en: "Nicaragua" },
  NL: { zh: "荷兰", en: "Netherlands" },
  NO: { zh: "挪威", en: "Norway" },
  NP: { zh: "尼泊尔", en: "Nepal" },
  NR: { zh: "瑙鲁", en: "Nauru" },
  NU: { zh: "纽埃", en: "Niue" },
  NZ: { zh: "新西兰", en: "New Zealand" },
  OM: { zh: "阿曼", en: "Oman" },
  PA: { zh: "巴拿马", en: "Panama" },
  PE: { zh: "秘鲁", en: "Peru" },
  PF: { zh: "法属波利尼西亚", en: "French Polynesia" },
  PG: { zh: "巴布亚新几内亚", en: "Papua New Guinea" },
  PH: { zh: "菲律宾", en: "Philippines" },
  PK: { zh: "巴基斯坦", en: "Pakistan" },
  PL: { zh: "波兰", en: "Poland" },
  PM: { zh: "圣皮埃尔和密克隆", en: "Saint Pierre and Miquelon" },
  PN: { zh: "皮特凯恩群岛", en: "Pitcairn Islands" },
  PR: { zh: "波多黎各", en: "Puerto Rico" },
  PS: { zh: "巴勒斯坦", en: "Palestine" },
  PT: { zh: "葡萄牙", en: "Portugal" },
  PW: { zh: "帕劳", en: "Palau" },
  PY: { zh: "巴拉圭", en: "Paraguay" },
  QA: { zh: "卡塔尔", en: "Qatar" },
  RE: { zh: "留尼汪", en: "Réunion" },
  RO: { zh: "罗马尼亚", en: "Romania" },
  RS: { zh: "塞尔维亚", en: "Serbia" },
  RU: { zh: "俄罗斯", en: "Russia" },
  RW: { zh: "卢旺达", en: "Rwanda" },
  SA: { zh: "沙特阿拉伯", en: "Saudi Arabia" },
  SB: { zh: "所罗门群岛", en: "Solomon Islands" },
  SC: { zh: "塞舌尔", en: "Seychelles" },
  SD: { zh: "苏丹", en: "Sudan" },
  SE: { zh: "瑞典", en: "Sweden" },
  SG: { zh: "新加坡", en: "Singapore" },
  SH: { zh: "圣赫勒拿", en: "Saint Helena" },
  SI: { zh: "斯洛文尼亚", en: "Slovenia" },
  SJ: { zh: "斯瓦尔巴和扬马延", en: "Svalbard and Jan Mayen" },
  SK: { zh: "斯洛伐克", en: "Slovakia" },
  SL: { zh: "塞拉利昂", en: "Sierra Leone" },
  SM: { zh: "圣马力诺", en: "San Marino" },
  SN: { zh: "塞内加尔", en: "Senegal" },
  SO: { zh: "索马里", en: "Somalia" },
  SR: { zh: "苏里南", en: "Suriname" },
  SS: { zh: "南苏丹", en: "South Sudan" },
  ST: { zh: "圣多美和普林西比", en: "São Tomé and Príncipe" },
  SV: { zh: "萨尔瓦多", en: "El Salvador" },
  SX: { zh: "荷属圣马丁", en: "Sint Maarten" },
  SY: { zh: "叙利亚", en: "Syria" },
  SZ: { zh: "斯威士兰", en: "Eswatini" },
  TC: { zh: "特克斯和凯科斯群岛", en: "Turks and Caicos Islands" },
  TD: { zh: "乍得", en: "Chad" },
  TF: { zh: "法属南方领地", en: "French Southern Territories" },
  TG: { zh: "多哥", en: "Togo" },
  TH: { zh: "泰国", en: "Thailand" },
  TJ: { zh: "塔吉克斯坦", en: "Tajikistan" },
  TK: { zh: "托克劳", en: "Tokelau" },
  TL: { zh: "东帝汶", en: "Timor-Leste" },
  TM: { zh: "土库曼斯坦", en: "Turkmenistan" },
  TN: { zh: "突尼斯", en: "Tunisia" },
  TO: { zh: "汤加", en: "Tonga" },
  TR: { zh: "土耳其", en: "Turkey" },
  TT: { zh: "特立尼达和多巴哥", en: "Trinidad and Tobago" },
  TV: { zh: "图瓦卢", en: "Tuvalu" },
  TW: { zh: "中国台湾", en: "Taiwan" },
  TZ: { zh: "坦桑尼亚", en: "Tanzania" },
  UA: { zh: "乌克兰", en: "Ukraine" },
  UG: { zh: "乌干达", en: "Uganda" },
  UM: { zh: "美国本土外小岛屿", en: "U.S. Outlying Islands" },
  US: { zh: "美国", en: "United States" },
  UY: { zh: "乌拉圭", en: "Uruguay" },
  UZ: { zh: "乌兹别克斯坦", en: "Uzbekistan" },
  VA: { zh: "梵蒂冈", en: "Vatican City" },
  VC: { zh: "圣文森特和格林纳丁斯", en: "Saint Vincent and the Grenadines" },
  VE: { zh: "委内瑞拉", en: "Venezuela" },
  VG: { zh: "英属维尔京群岛", en: "British Virgin Islands" },
  VI: { zh: "美属维尔京群岛", en: "U.S. Virgin Islands" },
  VN: { zh: "越南", en: "Vietnam" },
  VU: { zh: "瓦努阿图", en: "Vanuatu" },
  WF: { zh: "瓦利斯和富图纳", en: "Wallis and Futuna" },
  WS: { zh: "萨摩亚", en: "Samoa" },
  YE: { zh: "也门", en: "Yemen" },
  YT: { zh: "马约特", en: "Mayotte" },
  ZA: { zh: "南非", en: "South Africa" },
  ZM: { zh: "赞比亚", en: "Zambia" },
  ZW: { zh: "津巴布韦", en: "Zimbabwe" },
  // Codes outside the core ISO list that Cloudflare may still emit.
  XK: { zh: "科索沃", en: "Kosovo" },
};

/**
 * Curated English (Cloudflare spelling) → 中文 for cities likely to appear in
 * the traffic. Heavy on mainland China, plus HK/Macau/Taiwan and major global
 * trade hubs. Keys are EXACTLY as Cloudflare spells them (Title Case).
 */
export const CITY_ZH: Record<string, string> = {
  // --- Mainland China ---
  Shanghai: "上海",
  Beijing: "北京",
  Shenzhen: "深圳",
  Guangzhou: "广州",
  Ningbo: "宁波",
  Hangzhou: "杭州",
  Qingdao: "青岛",
  Tianjin: "天津",
  Suzhou: "苏州",
  Xiamen: "厦门",
  Dalian: "大连",
  Chengdu: "成都",
  Chongqing: "重庆",
  Wuhan: "武汉",
  Nanjing: "南京",
  Wuxi: "无锡",
  Foshan: "佛山",
  Dongguan: "东莞",
  Zhengzhou: "郑州",
  "Xi'an": "西安",
  Xian: "西安",
  Jinan: "济南",
  Yiwu: "义乌",
  Changsha: "长沙",
  Fuzhou: "福州",
  Hefei: "合肥",
  Kunming: "昆明",
  Shenyang: "沈阳",
  Harbin: "哈尔滨",
  Nanning: "南宁",
  Lianyungang: "连云港",
  Tangshan: "唐山",
  Weihai: "威海",
  Yantai: "烟台",
  Zhuhai: "珠海",
  Zhongshan: "中山",
  Wenzhou: "温州",
  Jiaxing: "嘉兴",
  Shaoxing: "绍兴",
  Taizhou: "台州",
  Nantong: "南通",
  Changzhou: "常州",
  Xuzhou: "徐州",
  Yancheng: "盐城",
  Quanzhou: "泉州",
  Zhangzhou: "漳州",
  Putian: "莆田",
  Jinhua: "金华",
  Huzhou: "湖州",
  Lishui: "丽水",
  Quzhou: "衢州",
  Zhoushan: "舟山",
  Linyi: "临沂",
  Weifang: "潍坊",
  Zibo: "淄博",
  Jining: "济宁",
  Dezhou: "德州",
  Rizhao: "日照",
  Liaocheng: "聊城",
  Binzhou: "滨州",
  Heze: "菏泽",
  Taian: "泰安",
  "Tai'an": "泰安",
  Zaozhuang: "枣庄",
  Langfang: "廊坊",
  Baoding: "保定",
  Shijiazhuang: "石家庄",
  Handan: "邯郸",
  Cangzhou: "沧州",
  Qinhuangdao: "秦皇岛",
  Hohhot: "呼和浩特",
  Baotou: "包头",
  Ordos: "鄂尔多斯",
  Taiyuan: "太原",
  Datong: "大同",
  Lanzhou: "兰州",
  Xining: "西宁",
  Yinchuan: "银川",
  Urumqi: "乌鲁木齐",
  "Ürümqi": "乌鲁木齐",
  Lhasa: "拉萨",
  Guiyang: "贵阳",
  Nanchang: "南昌",
  Ganzhou: "赣州",
  Jiujiang: "九江",
  Yingkou: "营口",
  Anshan: "鞍山",
  Changchun: "长春",
  Jilin: "吉林",
  Daqing: "大庆",
  Mianyang: "绵阳",
  Luoyang: "洛阳",
  Kaifeng: "开封",
  Xinxiang: "新乡",
  Nanyang: "南阳",
  Xuchang: "许昌",
  Hengyang: "衡阳",
  Zhuzhou: "株洲",
  Xiangtan: "湘潭",
  Yueyang: "岳阳",
  Changde: "常德",
  Guilin: "桂林",
  Liuzhou: "柳州",
  Beihai: "北海",
  Haikou: "海口",
  Sanya: "三亚",
  Huizhou: "惠州",
  Jiangmen: "江门",
  Zhaoqing: "肇庆",
  Maoming: "茂名",
  Zhanjiang: "湛江",
  Shantou: "汕头",
  Jieyang: "揭阳",
  Qingyuan: "清远",
  Yangjiang: "阳江",
  Heyuan: "河源",
  Shaoguan: "韶关",
  Meizhou: "梅州",
  Chaozhou: "潮州",
  Anqing: "安庆",
  Wuhu: "芜湖",
  Bengbu: "蚌埠",
  Maanshan: "马鞍山",
  "Ma'anshan": "马鞍山",
  Yangzhou: "扬州",
  Zhenjiang: "镇江",
  Huaian: "淮安",
  "Huai'an": "淮安",
  Suqian: "宿迁",
  // --- Hong Kong / Macau / Taiwan ---
  "Hong Kong": "香港",
  Kowloon: "九龙",
  "Central District": "中环",
  "Tsuen Wan": "荃湾",
  "Sha Tin": "沙田",
  Macau: "澳门",
  Macao: "澳门",
  Taipei: "台北",
  "New Taipei": "新北",
  Taichung: "台中",
  Tainan: "台南",
  Kaohsiung: "高雄",
  Hsinchu: "新竹",
  Taoyuan: "桃园",
  Keelung: "基隆",
  // --- East / Southeast Asia ---
  Singapore: "新加坡",
  Tokyo: "东京",
  Osaka: "大阪",
  Nagoya: "名古屋",
  Yokohama: "横滨",
  Kyoto: "京都",
  Kobe: "神户",
  Fukuoka: "福冈",
  Sapporo: "札幌",
  Seoul: "首尔",
  Busan: "釜山",
  Incheon: "仁川",
  Daegu: "大邱",
  "Ho Chi Minh City": "胡志明市",
  Hanoi: "河内",
  "Da Nang": "岘港",
  Haiphong: "海防",
  Bangkok: "曼谷",
  "Chiang Mai": "清迈",
  "Laem Chabang": "林查班",
  "Kuala Lumpur": "吉隆坡",
  "Port Klang": "巴生港",
  Penang: "槟城",
  "Johor Bahru": "新山",
  Jakarta: "雅加达",
  Surabaya: "泗水",
  Manila: "马尼拉",
  Cebu: "宿务",
  "Phnom Penh": "金边",
  Vientiane: "万象",
  Yangon: "仰光",
  "Colombo": "科伦坡",
  Dhaka: "达卡",
  "Chittagong": "吉大港",
  Karachi: "卡拉奇",
  Lahore: "拉合尔",
  Mumbai: "孟买",
  "New Delhi": "新德里",
  Delhi: "德里",
  Bangalore: "班加罗尔",
  Bengaluru: "班加罗尔",
  Chennai: "金奈",
  Kolkata: "加尔各答",
  Hyderabad: "海得拉巴",
  Pune: "浦那",
  "Phnom-Penh": "金边",
  // --- Middle East ---
  Dubai: "迪拜",
  "Abu Dhabi": "阿布扎比",
  Doha: "多哈",
  Riyadh: "利雅得",
  Jeddah: "吉达",
  Dammam: "达曼",
  "Jebel Ali": "杰贝阿里",
  "Tel Aviv": "特拉维夫",
  Istanbul: "伊斯坦布尔",
  Ankara: "安卡拉",
  Tehran: "德黑兰",
  // --- Europe ---
  Rotterdam: "鹿特丹",
  Amsterdam: "阿姆斯特丹",
  Hamburg: "汉堡",
  Berlin: "柏林",
  Munich: "慕尼黑",
  Frankfurt: "法兰克福",
  Bremen: "不来梅",
  Antwerp: "安特卫普",
  Brussels: "布鲁塞尔",
  London: "伦敦",
  Felixstowe: "费利克斯托",
  Southampton: "南安普敦",
  Paris: "巴黎",
  "Le Havre": "勒阿弗尔",
  Marseille: "马赛",
  Madrid: "马德里",
  Barcelona: "巴塞罗那",
  Valencia: "瓦伦西亚",
  Lisbon: "里斯本",
  Milan: "米兰",
  Genoa: "热那亚",
  Rome: "罗马",
  Naples: "那不勒斯",
  Athens: "雅典",
  Piraeus: "比雷埃夫斯",
  Gdansk: "格但斯克",
  Warsaw: "华沙",
  Vienna: "维也纳",
  Zurich: "苏黎世",
  Geneva: "日内瓦",
  Stockholm: "斯德哥尔摩",
  Gothenburg: "哥德堡",
  Copenhagen: "哥本哈根",
  Oslo: "奥斯陆",
  Helsinki: "赫尔辛基",
  Dublin: "都柏林",
  Moscow: "莫斯科",
  "Saint Petersburg": "圣彼得堡",
  // --- North America ---
  "Los Angeles": "洛杉矶",
  "Long Beach": "长滩",
  "New York": "纽约",
  Newark: "纽瓦克",
  "San Francisco": "旧金山",
  "San Jose": "圣何塞",
  Seattle: "西雅图",
  Tacoma: "塔科马",
  Oakland: "奥克兰",
  Chicago: "芝加哥",
  Houston: "休斯顿",
  Dallas: "达拉斯",
  Atlanta: "亚特兰大",
  Miami: "迈阿密",
  Boston: "波士顿",
  "Washington": "华盛顿",
  Savannah: "萨凡纳",
  Charleston: "查尔斯顿",
  Norfolk: "诺福克",
  Vancouver: "温哥华",
  Toronto: "多伦多",
  Montreal: "蒙特利尔",
  "Mexico City": "墨西哥城",
  Manzanillo: "曼萨尼约",
  // --- Oceania / Africa / South America ---
  Sydney: "悉尼",
  Melbourne: "墨尔本",
  Brisbane: "布里斯班",
  Perth: "珀斯",
  Auckland: "奥克兰",
  "Santos": "桑托斯",
  "Sao Paulo": "圣保罗",
  "São Paulo": "圣保罗",
  "Rio de Janeiro": "里约热内卢",
  "Buenos Aires": "布宜诺斯艾利斯",
  Santiago: "圣地亚哥",
  Lima: "利马",
  "Cape Town": "开普敦",
  Durban: "德班",
  Johannesburg: "约翰内斯堡",
  Lagos: "拉各斯",
  Cairo: "开罗",
  "Port Said": "塞得港",
  Casablanca: "卡萨布兰卡",
  Mombasa: "蒙巴萨",
};

/**
 * China provinces / municipalities / autonomous regions, English → 中文.
 * Cloudflare reports region as the English province name for CN traffic.
 */
export const REGION_ZH: Record<string, string> = {
  Guangdong: "广东",
  Zhejiang: "浙江",
  Jiangsu: "江苏",
  Shandong: "山东",
  Beijing: "北京",
  Shanghai: "上海",
  Fujian: "福建",
  Sichuan: "四川",
  Hubei: "湖北",
  Hunan: "湖南",
  Henan: "河南",
  Hebei: "河北",
  Liaoning: "辽宁",
  Anhui: "安徽",
  Shaanxi: "陕西",
  Yunnan: "云南",
  Guangxi: "广西",
  Jiangxi: "江西",
  Heilongjiang: "黑龙江",
  Jilin: "吉林",
  Shanxi: "山西",
  Gansu: "甘肃",
  Guizhou: "贵州",
  Hainan: "海南",
  Tianjin: "天津",
  Chongqing: "重庆",
  "Inner Mongolia": "内蒙古",
  Xinjiang: "新疆",
  Tibet: "西藏",
  Ningxia: "宁夏",
  Qinghai: "青海",
};

const UNKNOWN_LABEL = "未知 Unknown";

/**
 * Compose a bilingual label "<zh> <en>". When zh is empty or equals en, only the
 * single non-empty value is returned (no duplication / no stray space).
 */
function bilingual(zh: string, en: string): string {
  const z = zh.trim();
  const e = en.trim();
  if (!z) return e;
  if (!e || z === e) return z;
  return `${z} ${e}`;
}

/**
 * Country/region label from an ISO 3166-1 alpha-2 code.
 *   "CN" -> "中国 China", "HK" -> "中国香港 Hong Kong"
 *   "Unknown"/"XX"/"" -> "未知 Unknown"
 *   unknown code -> the code itself (e.g. "ZZ")
 */
export function countryLabel(code: string): string {
  const raw = (code ?? "").trim();
  if (!raw || raw.toUpperCase() === "UNKNOWN" || raw.toUpperCase() === "XX") {
    return UNKNOWN_LABEL;
  }
  const hit = COUNTRY_NAMES[raw.toUpperCase()];
  if (!hit) return raw;
  return bilingual(hit.zh, hit.en);
}

/**
 * City label from the English (Cloudflare) city name.
 *   "Shanghai" -> "上海 Shanghai", "Hong Kong" -> "香港 Hong Kong"
 *   unmapped city -> the English name as-is
 *   ""/"Unknown" -> "未知 Unknown"
 */
export function cityLabel(city: string): string {
  const raw = (city ?? "").trim();
  if (!raw || raw.toLowerCase() === "unknown") return UNKNOWN_LABEL;
  const zh = CITY_ZH[raw];
  if (!zh) return raw;
  return bilingual(zh, raw);
}

/**
 * Region (China province / municipality) label from the English name.
 *   "Guangdong" -> "广东 Guangdong"
 *   unmapped region -> the English name
 *   "" -> ""
 */
export function regionLabel(region: string): string {
  const raw = (region ?? "").trim();
  if (!raw) return "";
  const zh = REGION_ZH[raw];
  if (!zh) return raw;
  return bilingual(zh, raw);
}
